import { useState } from "react";
import { useLazyQuery } from "@apollo/client/react";
import { ACT_AS_TARGETS } from "graphql/user";
import { useUserInfo } from "hocs/UserInfoContext";
import { ACT_AS_ROLES, formatRoleLabel, useActAs } from "./useActAs";

const GROUPS = [
  { type: "BANK", label: "Banks" },
  { type: "COMPANY", label: "Companies" },
  { type: "ORGANISATION", label: "Organisations" },
];

const matches = (target, filter) =>
  !filter || target.name.toLowerCase().includes(filter.toLowerCase());

/**
 * "Act as" section of the user popout menu, for users whose whoami.canActAs is true. Lists every
 * bank / company / organisation (fetched when the section is opened) and switches with the backend
 * actAs mutation — no second login, no passwords. While acting, offers the way back.
 */
const ActAsMenu = () => {
  const { actingAs, canActAs } = useUserInfo();
  const { actAs, stopActingAs, busy, error } = useActAs();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [loadTargets, { data, loading, error: loadError }] = useLazyQuery(ACT_AS_TARGETS);

  if (!canActAs) return null;

  const toggle = () => {
    if (!open && !data) loadTargets();
    setOpen((prev) => !prev);
  };

  const targets = data?.actAsTargets || [];
  const isCurrent = (target) =>
    actingAs && actingAs.type === target.type && String(actingAs.id) === String(target.id);

  return (
    <div className="pt-1 mt-1 border-t border-border" data-testid="act-as-menu">
      <p className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        Act as
      </p>

      {actingAs && (
        <div className="px-3 py-2 mb-1 rounded bg-warning-light text-warning-light-foreground">
          <p className="text-xs">
            Acting as <span className="font-medium">{actingAs.name}</span> ·{" "}
            {formatRoleLabel(actingAs.role)}
          </p>
          <button
            type="button"
            onClick={stopActingAs}
            disabled={busy}
            className="mt-1 text-xs font-medium underline disabled:opacity-50"
          >
            Stop acting as {actingAs.name}
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent rounded transition-colors duration-150"
      >
        {open ? "Hide entities" : "Choose a bank, company or organisation…"}
      </button>

      {open && (
        <div className="px-2 pb-2">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name"
            aria-label="Filter entities"
            className="w-full mb-2 px-2 py-1.5 text-sm rounded border border-border bg-background text-foreground"
          />
          {loading && <p className="px-1 py-1 text-xs text-muted-foreground">Loading…</p>}
          {loadError && (
            <p className="px-1 py-1 text-xs text-destructive">Could not load entities.</p>
          )}
          {error && <p className="px-1 py-1 text-xs text-destructive">{error}</p>}
          <div className="max-h-72 overflow-y-auto">
            {GROUPS.map(({ type, label }) => {
              const rows = targets.filter((t) => t.type === type && matches(t, filter));
              if (rows.length === 0) return null;
              return (
                <div key={type} className="mb-2">
                  <p className="px-1 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {label}
                  </p>
                  {rows.map((target) => (
                    <div
                      key={`${target.type}-${target.id}`}
                      className={`flex items-center justify-between gap-2 px-1 py-1 rounded ${
                        isCurrent(target) ? "bg-accent/50" : "hover:bg-accent"
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{target.name}</p>
                        {target.status && target.status !== "APPROVED" && (
                          <p className="text-[10px] text-muted-foreground">
                            {formatRoleLabel(target.status)}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        {["admin", "user"].map((kind) => {
                          const role = ACT_AS_ROLES[target.type][kind];
                          const active = isCurrent(target) && actingAs.role === role;
                          return (
                            <button
                              key={kind}
                              type="button"
                              disabled={busy || active}
                              onClick={() => actAs({ type: target.type, id: target.id, role })}
                              aria-label={`Act as ${target.name} ${formatRoleLabel(role)}`}
                              className={`px-2 py-0.5 text-[11px] rounded border transition-colors duration-150 ${
                                active
                                  ? "bg-primary text-primary-foreground border-primary cursor-default"
                                  : "border-border text-foreground hover:bg-primary hover:text-primary-foreground"
                              } disabled:opacity-60`}
                            >
                              {kind === "admin" ? "Admin" : "User"}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            {!loading && !loadError && data && targets.filter((t) => matches(t, filter)).length === 0 && (
              <p className="px-1 py-1 text-xs text-muted-foreground">No matches.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActAsMenu;
